import type { MetricsTranslations } from "../interfaces/metrics";

export const metricsTranslations: MetricsTranslations = {
  page_title: "Metrics",
  page_description: "The money the wallets move, OTC and the experiments, across every organization.",
  tab_otc: "OTC",
  tab_experiments: "Experiments",
  refresh: "Refresh",
  period_label: "Period",
  period_option: "{{days}} days",
  window_caption: "From {{from}} to {{to}}, compared with the {{days}} days before.",
  error_title: "We couldn't load the metrics",
  error_text: "Try again in a moment.",
  error_retry: "Try again",
  kpi_volume: "Volume",
  kpi_ticket: "Average ticket",
  change_vs_previous: "{{change}} vs the previous {{days}} days",
  change_none: "No data in the previous period",
  top_hint: "By volume in the period.",
  col_user: "User",
  col_organization: "Organization",
  col_transactions: "Transactions",
  col_volume: "Volume",
  col_ticket: "Average ticket",
  via_api: "Through the API",
  top_empty: "Nobody traded in this period.",
  experiments_description: "A/B tests: each user always sees the same version while the test runs.",
  experiments_empty: "There are no experiments yet.",
  col_experiment: "Experiment",
  col_status: "Status",
  col_split: "Split",
  col_users: "Users",
  col_started: "Started",
  status_RUNNING: "Running",
  status_PAUSED: "Paused",
  status_ROLLED_OUT: "On for everyone",
  experiment_otc_volume_discount: "Volume-discount banner (USDT buy and sell)",
  variant_otc_volume_discount_control: "Without banner",
  variant_otc_volume_discount_treatment: "With banner",
  variant_users: "{{variant}}: {{users}}",
  detail_back: "Experiments",
  detail_since: "Since {{date}}",
  detail_until: "Until {{date}}",
  detail_not_found: "We couldn't find that experiment.",
  detail_no_metric: "This experiment has no metrics yet.",
  detail_how_to_read:
    "Conversion is the share of users who bought at least once since joining the test. With fewer than 30 users per group, read the difference as a trend.",
  group_users: "Users",
  group_conversion_rate: "Conversion",
  group_average: "Average value",
  significance_significant: "Significant difference",
  significance_trend: "Not conclusive yet",
  significance_not_enough: "Not enough data",
  action_pause: "Pause",
  action_resume: "Resume",
  action_rollout: "Turn on for everyone",
  confirm_pause_title: "Pause the experiment?",
  confirm_pause_text:
    "Everyone will see the control version. Assignments are kept and the analysis stops here.",
  confirm_resume_title: "Resume the experiment?",
  confirm_resume_text: "Each user goes back to their group and new users are assigned at random.",
  confirm_rollout_title: "Turn it on for everyone?",
  confirm_rollout_text:
    "Everyone will see the “{{variant}}” version, including those in the other group. The analysis closes here.",
  confirm_cancel: "Cancel",
  toast_updated: "Experiment updated",
  toast_failed: "We couldn't update the experiment",
  action_split: "Change split",
  detail_split: "Split: {{split}}",
  split_title: "Change split",
  split_description: "The share of new users who get each version.",
  split_presets_label: "New users in “{{variant}}”",
  split_input_label: "{{variant}} (%)",
  split_invalid_number: "Enter a whole number from 0 to 100.",
  split_total: "Total: {{total}}%",
  split_total_error: "Adds up to {{total}}%; it has to be 100%.",
  split_keep_note:
    "Users who already have a version keep it. The new split applies to users who arrive from now on.",
  split_save: "Save split",
  toast_split_updated: "Split updated",
  toast_split_failed: "We couldn't change the split",
  user_section_title: "Version per user",
  user_section_hint:
    "Search for someone by name or email to see which version they get, even before their first quote, and change it. A fixed version always shows, even while the experiment is paused, and is not counted in the results.",
  user_not_found: "We couldn't find a user with that email.",
  user_lookup_failed: "We couldn't look up that user.",
  user_sees: "Sees: {{variant}}",
  source_forced: "Version fixed by staff.",
  source_assigned: "Assigned at random on {{date}}.",
  source_not_assigned: "Hasn't quoted yet: this is the version it will get.",
  source_staff: "Staff: sees the version under test.",
  source_paused: "The experiment is paused: sees the control version.",
  source_rolled_out: "The experiment is on for everyone.",
  user_force: "Fix to “{{variant}}”",
  force_otc_volume_discount_treatment: "Turn the banner on",
  force_otc_volume_discount_control: "Turn the banner off",
  user_release: "Back to random",
  user_release_hint: "Back to random, it will see “{{variant}}”.",
  toast_forced: "{{email}} now sees “{{variant}}”",
  toast_released: "{{email}} is back to random",
  toast_override_failed: "We couldn't change the version",
  overrides_title: "Users with a fixed version",
  overrides_since: "Since {{date}}",
  group_average_otc_trade: "Average ticket",
  overview_volume: "Total volume",
  overview_operations: "Operations",
  overview_users: "Active users",
  daily_title_volume: "Volume per day",
  daily_title_operations: "Operations per day",
  daily_hint: "Each day, purchases (USDT and card loads, stacked) next to sales.",
  daily_measure_label: "Measure",
  side_buy_short: "Purchases",
  side_sell_short: "Sales",
  side_card_top_up_short: "Cards",
  measure_volume: "Volume",
  measure_operations: "Operations",
  sides_title: "Purchases, cards and sales",
  sides_hint: "Each side against its previous {{days}} days.",
  col_metric: "Metric",
  top_title: "Top traders",
  top_tab_buy: "Buy the most",
  top_tab_sell: "Sell the most",
  top_tab_card_top_up: "Load cards the most",
  verdict_title: "Result so far",
  verdict_headline_up: "“{{variant}}” converts {{change}} more than “{{against}}”",
  verdict_headline_down: "“{{variant}}” converts {{change}} less than “{{against}}”",
  verdict_headline_equal: "“{{variant}}” and “{{against}}” convert the same",
  verdict_rates: "{{variantRate}} of “{{variant}}” users traded, against {{againstRate}} of “{{against}}”.",
  verdict_significant: "The difference is significant (p = {{p}}): unlikely to be chance.",
  verdict_trend: "It may still be chance (p = {{p}}). Let the test run.",
  verdict_not_enough: "Not enough data: each group needs at least 30 users. Read the difference as a trend.",
  verdict_empty: "Nobody has traded in the test yet.",
  col_difference: "Difference",
  currency_label: "Currency",
  currency_info_label: "What USD means here",
  currency_info:
    "In USD you see the USDT bought and sold (1 USDT ≈ 1 USD). The price per USD is always in pesos.",
  info_label: "What it covers: {{metric}}",
  sides_split: "Purchases {{buy}} · Cards {{cards}} · Sales {{sell}}",
  info_volume:
    "Purchases, card loads and sales completed in the period. A purchase or a card load adds the pesos the client paid, fees included; a sale, the pesos we paid to their key, before our commission. In USD, the USDT delivered and received.",
  overview_net_flow: "Net flow",
  info_net_flow:
    "Purchases and card loads minus sales, in the chosen currency. Positive: clients took out more USDT than they brought in. Negative: they brought in more than they took out.",
  net_flow_buy: "They bought more than they sold",
  net_flow_sell: "They sold more than they bought",
  net_flow_even: "They bought as much as they sold",
  info_operations:
    "Purchases paid (USDT delivered), card loads delivered (USDT sent to the card) and sales completed (pesos credited) in the period. Those that failed or expired are not counted here: see “Not completed”.",
  overview_users_new: "New: {{users}}",
  info_users:
    "Whoever completed at least one purchase, card load or sale in the period; an operation through the API counts as its organization. New: their first operation ever fell in this period.",
  daily_difference: "Purchases − sales: {{value}}",
  daily_purchases_total: "Purchases",
  daily_segment_usdt: "USDT purchases",
  daily_segment_cards: "Card loads",
  daily_cards_share: "Card loads: {{share}} of the period's purchases.",
  side_label: "Side",
  prices_title: "Price per USD",
  prices_hint: "What the client paid or received per USDT, fees included, weighted by volume.",
  prices_buy: "Bought at",
  prices_sell: "Sold at",
  prices_card_top_up: "Loaded cards at",
  prices_spread: "Spread",
  prices_spread_meta: "{{percent}} over the sale price",
  info_spread:
    "Purchase price minus sale price: the gap between who buys and who sells, our fees and the liquidity margin included.",
  prices_empty: "No purchases or sales in this period: there is no price to show.",
  info_price:
    "Pesos per USDT, fees included, weighted by volume. Purchase: pesos debited ÷ USDT received. Card load: pesos debited ÷ USDT sent to the card, before the card's deposit fee. Sale: pesos credited ÷ USDT sold.",
  info_volume_side:
    "Purchase and card load: the pesos the client paid, fees included (in USD, the USDT delivered). Sale: the pesos we paid to their key, before our commission (in USD, the USDT received).",
  info_ticket: "Volume divided by operations, in the chosen currency.",
  row_duration: "Actual time",
  duration_p90: "9 in 10 under {{time}}",
  info_duration:
    "Median of the period. Purchase: from creation until the USDT is delivered. Card load: from creation until the USDT reaches the card's address, without the time the card takes to credit it. Sale: from the deposit arriving until we pay the pesos; it can carry up to a minute from how often we check it.",
  row_estimate: "Time the client sees",
  estimate_network: "{{network}}: {{time}}",
  estimate_none: "No estimate",
  info_estimate:
    "What the purchase or sale screen shows the client today, per network. It comes from recent operations, not from this period. Card loads show their own wait, on the card.",
  row_not_completed: "Not completed",
  not_completed_rate: "{{rate}} of those that ended",
  info_not_completed:
    "Purchases and card loads that failed, were cancelled or reversed, and sales that expired without a deposit or were refunded, ending in the period. The percentage is over everything that ended, completed or not.",
  reason_count: "{{reason}}: {{total}}",
  reason_failed: "Failed",
  reason_cancelled: "Cancelled",
  reason_reversed: "Reversed",
  reason_expired: "Expired without deposit",
  reason_refunding: "Refunding",
  reason_refunded: "Refunded",
  users_split: "New: {{new}} · Returning: {{returning}}",
  info_users_side:
    "Whoever completed at least one operation on that side; through the API, the organization counts. New: their first operation on that side fell in this period.",
  conversion_split: "{{converted}} of {{quoted}} who quoted",
  info_conversion:
    "Per user, not per quote: someone who quoted ten times and bought once converted. Organization users count; staff do not, since their quotes are mostly tests. Card loads are not quoted on the desk, so they have no conversion.",
  intent_title: "Who quoted and what they traded",
  intent_hint: "Each user's largest quote against what they ended up trading in the period.",
  intent_quoted: "Quoted",
  intent_converted: "Traded",
  intent_captured: "Captured",
  intent_captured_meta: "{{traded}} of {{quoted}} quoted",
  info_captured:
    "What the quoters traded, against the sum of each one's largest quote. It goes past 100 % when they traded more than they quoted.",
  intent_list_hint: "The 10 with the largest quote.",
  col_quotes: "Quotes",
  col_max_quote: "Largest quote",
  col_traded: "Traded",
  quoter_traded: "Traded",
  quoter_not_traded: "Didn't trade",
  quoter_last: "Last quote: {{date}}",
  intent_empty: "Nobody quoted in this period.",
  user_search_label: "User",
  user_search_placeholder: "Name or email",
  user_search_hint: "Type at least 3 letters.",
  user_search_searching: "Searching…",
  user_search_empty: "Nobody matches that search.",
  user_search_failed: "We couldn't search. Try again.",
  user_would_see: "Would get: {{variant}}",
  summary_title: "Summary by version",
  col_variant: "Version",
  summary_baseline: "Baseline",
  tab_tpv: "TPV",
  reason_rejected: "Rejected",
  tpv_overview_tpv: "TPV",
  tpv_info_tpv:
    "Confirmed collections plus paid payouts, in dollars at the rate of each operation's own day. Swaps and Coloca top-ups are not added.",
  tpv_split: "In {{in}} · out {{out}}",
  tpv_overview_revenue: "Coloca revenue",
  tpv_info_revenue:
    "Coloca's commission on collections and payouts, the conversion fee (cross-currency payouts and swaps) and the desk execution result of USDT purchases, not yet signed off by finance. Provider costs and taxes Coloca absorbs are not subtracted.",
  tpv_take_rate_value: "Take rate {{rate}}",
  tpv_ticket_value: "Average ticket {{ticket}}",
  tpv_overview_organizations: "Active organizations",
  tpv_info_organizations:
    "Organizations with at least one collection or payout in the period. New: their first operation ever falls in the period.",
  tpv_organizations_new: "{{organizations}} new",
  tpv_flow_title: "Where the money comes in and goes out",
  tpv_flow_hint:
    "The TPV of the period per product: what comes into the wallets on the left, what goes out on the right.",
  tpv_flow_in: "In",
  tpv_flow_out: "Out",
  tpv_flow_net_in: "{{amount}} more came in than went out",
  tpv_flow_net_out: "{{amount}} more went out than came in",
  tpv_flow_net_even: "As much came in as went out",
  tpv_flow_empty: "No collections or payouts in this period.",
  tpv_flow_aria: "{{in}} came in and {{out}} went out",
  tpv_flow_share: "{{share}} of what moved",
  tpv_product_collection: "Collections",
  tpv_product_otc_sell: "USDT sales",
  tpv_product_payout: "Payouts",
  tpv_product_otc_buy: "USDT purchases",
  tpv_product_card_top_up: "Card top-ups",
  tpv_daily_title_volume: "TPV per day",
  tpv_daily_title_operations: "Operations per day",
  tpv_daily_hint: "Collections and payouts of each day, in dollars at that day's rate.",
  tpv_series_in: "Collections",
  tpv_series_out: "Payouts",
  tpv_daily_net: "Net: {{value}}",
  tpv_flows_title: "Flows",
  tpv_flows_hint:
    "Collections and payouts make the TPV; swaps and Coloca top-ups are apart. Each figure against the previous {{days}} days.",
  tpv_flow_payins: "Collections",
  tpv_flow_payouts: "Payouts",
  tpv_flow_fx: "Swaps",
  tpv_flow_top_ups: "Coloca top-ups",
  tpv_row_operations: "Operations",
  tpv_info_operations: "Confirmed collections, paid payouts, swaps and top-ups, by when they completed.",
  tpv_row_volume: "Volume",
  tpv_info_volume:
    "In dollars at the rate of each operation's day: what the payer sent on a collection, what left the wallet on a payout or a swap, conversion fee included.",
  tpv_row_ticket: "Average ticket",
  tpv_info_ticket: "Volume over the operations converted to dollars.",
  tpv_row_revenue: "Revenue",
  tpv_revenue_service: "Commission {{amount}}",
  tpv_revenue_conversion: "Conversion {{amount}}",
  tpv_revenue_fx_execution: "OTC execution {{amount}}",
  tpv_row_take_rate: "Take rate",
  tpv_info_take_rate: "Revenue over volume. On the total, all revenue (swaps included) over the TPV.",
  tpv_row_organizations: "Organizations",
  tpv_row_not_completed: "Not completed",
  tpv_info_not_completed:
    "Collections rejected, failed or reversed and payouts failed, cancelled or reversed, by when they ended. Collections nobody paid that expired are counted apart.",
  tpv_row_expired: "Expired collections",
  tpv_info_expired:
    "A QR code or CLABE nobody paid, closed when it expired. Not a failure: a collection that was not used.",
  tpv_row_in_flight: "In flight now",
  tpv_info_in_flight:
    "Collections detected but not confirmed and payouts created or with the provider, right now, at today's rate.",
  tpv_in_flight_value: "{{operations}} · {{amount}}",
  tpv_unconverted: "{{operations}} without a rate, left out of the dollars",
  tpv_corridors_title: "Products and corridors",
  tpv_corridors_hint: "Every collection and payout per product, rail, country and currency, largest first.",
  tpv_col_product: "Product",
  tpv_col_rail: "Rail",
  tpv_col_country: "Country",
  tpv_col_currency: "Currency",
  tpv_col_operations: "Operations",
  tpv_col_volume: "Volume",
  tpv_col_revenue: "Revenue",
  tpv_col_share: "Share",
  tpv_col_change: "Change",
  tpv_direction_in: "In",
  tpv_direction_out: "Out",
  tpv_corridors_empty: "No collections or payouts in this period.",
  tpv_fx_pairs_title: "Swaps per pair",
  tpv_col_pair: "Pair",
  tpv_custody_title: "Balances held",
  tpv_custody_hint:
    "What the payment wallets hold today, valued at the latest known rate. The net change comes from the ledger.",
  tpv_custody_available: "Available",
  tpv_info_custody_available: "Balance the organizations can use now.",
  tpv_custody_pending: "Pending",
  tpv_info_custody_pending: "Collections detected but not confirmed yet.",
  tpv_custody_blocked: "Blocked",
  tpv_info_custody_blocked: "Reserved by payouts still in flight; it has already left the available balance.",
  tpv_custody_wallets: "Wallets",
  tpv_info_custody_wallets:
    "Payment wallets of every organization. Active: an organization and currency with a collection, payout or swap in the period.",
  tpv_custody_wallets_meta: "{{active}} active · {{new}} new",
  tpv_col_net_change: "Net change",
  tpv_info_net_change:
    "Credits minus debits of the wallets in that currency over the period, from the ledger. Reversals and adjustments are included, so it does not reconcile with today's balances.",
  tpv_custody_wallet_count: "{{wallets}} wallets",
  tpv_custody_no_rate: "No rate",
  tpv_custody_empty: "There are no payment wallets.",
  tpv_col_tpv: "TPV",
  tpv_col_payins: "Collections",
  tpv_col_payouts: "Payouts",
  tpv_unnamed_organization: "No name",
  tpv_rates_title: "Rates to dollars",
  tpv_rates_info:
    "Each amount is converted at the dollar rate of its own day, from the rate history. USD and USDT count 1. A day without its own rate takes the nearest known day's.",
  tpv_rates_filled: "{{share}} of the days with operations used the nearest day's rate.",
  tpv_rates_exact: "Every day with operations has its own rate.",
  tpv_rates_missing: "No rate, left out of the dollars: {{currencies}}.",
  tpv_rates_latest: "Latest rate: {{date}}.",
  tpv_filter_label: "Organization",
  tpv_filter_all: "Every organization",
  tpv_filter_all_hint: "Every figure adds up every organization.",
  tpv_filter_org_hint:
    "Every figure is this organization's alone: flows, corridors, balances and the daily series.",
  tpv_organizations_title: "Organizations",
  tpv_organizations_hint:
    "Every one with a collection, payout or swap in this period or the one before. Those that stopped operating are at the bottom.",
  tpv_organizations_search: "Search by name or id",
  tpv_organizations_sort_label: "Sort by",
  tpv_sort_tpv: "TPV",
  tpv_sort_operations: "Operations",
  tpv_sort_revenue: "Revenue",
  tpv_organizations_show_all: "Show all ({{organizations}})",
  tpv_organizations_show_less: "Show only the first 10",
  tpv_organizations_empty: "No organization operated in this period.",
  tpv_organizations_no_match: "No organization matches “{{term}}”.",
  tpv_col_transactions: "Transactions",
  tpv_previous_value: "Before {{amount}}",
  tpv_action_only_this: "Only this one",
  tpv_action_only_this_aria: "Show the page for {{organization}} only",
  tpv_action_payins: "Collections ({{operations}})",
  tpv_action_payouts: "Payouts ({{operations}})",
  tpv_action_view_transactions: "View transactions",
  tpv_corridors_link_note:
    "“View transactions” filters by rail: on Bre-B the USDT sales show too, and on USDT purchases and card top-ups come together.",
};
